
import BaseController from './BaseController';

import FileTypes from '../FileTypes';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader.js';

const scaleRate = 0.001;

export default class PathController extends BaseController {
  constructor (baseUrl, canvas, theme, options) {
    super(baseUrl, canvas, theme, options);
    this.pathJson = [];
    this.shadowPlane.material.opacity = 0;
  }

  get fileType () {
    return FileTypes.PATH;
  }

  addZ (pathsArray) {
    const z = { z: 0 };
    if (pathsArray.length > 0 && !Array.isArray(pathsArray[0])) {
      pathsArray.forEach((path) => {
        path = Object.assign(path, z);
      });
    } else if (pathsArray.length > 0 && Array.isArray(pathsArray[0])) {
      pathsArray.forEach((pathGroup) => {
        pathGroup.forEach((path) => {
          path = Object.assign(path, z);
        });
      });
    }
  }

  groupLine (coordinateArray) {
    let group = new THREE.Group();
    const material = new THREE.LineBasicMaterial({
      color: 0x0000ff,
      opacity: 0,
      transparent: true,
      depthWrite: false,
      depthTest: false
    });

    for (let i = 0; i < coordinateArray.length; i++) {
      let points = [];

      if (i === coordinateArray.length - 1) {
        // while last point -> connect to the first point
        points.push(new THREE.Vector3(coordinateArray[i].x, coordinateArray[i].y, coordinateArray[i].z));
        points.push(new THREE.Vector3(coordinateArray[0].x, coordinateArray[0].y, coordinateArray[0].z));
      } else {
        points.push(new THREE.Vector3(coordinateArray[i].x, coordinateArray[i].y, coordinateArray[i].z));
        points.push(new THREE.Vector3(coordinateArray[i + 1].x, coordinateArray[i + 1].y, coordinateArray[i + 1].z));
      }

      let geo = new THREE.BufferGeometry().setFromPoints(points);
      let line = new THREE.Line(geo, material);
      group.add(line);
    }

    // group.rotation.set(Math.PI * 0.5, 0, 0)
    // group.scale.set(scaleRate, scaleRate, scaleRate)
    return group;
  }

  makeShape (coordinateArray) {

    if (
      !Array.isArray(coordinateArray) ||
      coordinateArray.length === 0 ||
      !('x' in coordinateArray[0]) ||
      !('y' in coordinateArray[0])
    ) {
      // Skip invalid input
      return null;
    }
    let shape = new THREE.Shape();
    shape.moveTo(coordinateArray[0].x, coordinateArray[0].y);

    for (let j = 0; j < coordinateArray.length; j++) {
      if (j === coordinateArray.length - 1) {
        // while last point -> connect to the first point
        shape.lineTo(coordinateArray[0].x, coordinateArray[0].y);
      } else {
        shape.lineTo(coordinateArray[j + 1].x, coordinateArray[j + 1].y);
      }
    }

    return shape;
  }

  makeOutline (coordinateArray) { // making outlines based on coordinate array
    let points = [];
    for (let i = 0; i < coordinateArray.length; i++) {
      let vertice = new THREE.Vector2(coordinateArray[i].x, coordinateArray[i].y);
      points.push(vertice);
    }

    let geometry = new THREE.BufferGeometry().setFromPoints(points);
    // LineLoop generates closed curve (connecting points[points.length-1] to points[0])
    let outline = new THREE.LineLoop(geometry, new THREE.LineBasicMaterial({ color: 0x00A9B5 }));

    return outline;
  }

  addLinesToMesh (mesh, outlines, topOutlines, lineType) {
    switch (lineType) {
      case 'main':
        for (let line of outlines) { // add outlines to mesh
          mesh.add(line);
        }
        for (let line of topOutlines) {
          mesh.add(line);
        }
        break;
      case 'pocket':
      case 'singlePath':
        for (let outlineArray of outlines) {
          for (let line of outlineArray) {
            mesh.add(line);
          }
        }
        for (let line of topOutlines) {
          mesh.add(line);
        }
        break;
    }
  }

  extrude (shapes, depth, pocketShapes, pocketDepth, singleShapes, singleDepth, lines, pocketLines, singleLines) {
    const extrudeSettings = {
      depth: depth,
      bevelEnabled: false,
      steps: 1
    };

    // let group = new THREE.Group()
    let posGeo = new THREE.ExtrudeGeometry(shapes[0], extrudeSettings); // ex_path
    let negGeo = new THREE.Geometry();

    let pocketGeo = new THREE.Geometry();
    let innerPocketGeo = new THREE.Geometry();
    let singlePathGeo = new THREE.Geometry();
    // let posMesh = new THREE.Mesh(posGeo, posMat)
    // meshes.push(posMesh)

    if (shapes.length > 1) {
      for (let i = 1; i < shapes.length; i++) {
        let inPathGeo = new THREE.ExtrudeGeometry(shapes[i], extrudeSettings);
        inPathGeo.translate(0, 0, 0.1);
        negGeo.merge(inPathGeo);
      }
    }

    let topLines = [];
    for (let line of lines) { // making cloned outlines and move to top & push to topLines[]
      let cloned = line.clone(); // making cloned outline
      cloned.translateZ(depth); // moving cloned one to the top
      topLines.push(cloned); // push cloned one to topLines[]
    }

    let topPocketLines = []; // making cloned outlines and move to top & push to topPocketLines[]
    if (pocketShapes.length > 0) {
      for (let i = 0; i < pocketShapes.length; i++) {
        extrudeSettings.depth = pocketDepth[i];
        for (let o = 0; o < pocketShapes[i].length; o++) {
          let pocketExtrude = new THREE.ExtrudeGeometry(pocketShapes[i][o], extrudeSettings);
          pocketExtrude.translate(0, 0, depth - pocketDepth[i] + 0.1);
          let cloned = pocketLines[i][o].clone(); // making a clone of pocket outline
          cloned.translateZ(depth); // move cloned one to top
          topPocketLines.push(cloned); // push cloned one to topPocketLines
          pocketLines[i][o].translateZ(depth - pocketDepth[i] + 0.1); // move the original outline to correct height (from bottom)
          // extrudeGeo.merge(nextExtrude)
          if (o === 0) {
            // negGeo.merge(pocketExtrude)
            pocketGeo.merge(pocketExtrude);
          } else {
            // posGeo.merge(pocketExtrude)
            innerPocketGeo.merge(pocketExtrude);
          }
        }
      }
    }

    let topSingleLines = []; // making cloned outlines and move to top & push to topSingleLines[]
    if (singleShapes.length > 0) {
      for (let i = 0; i < singleShapes.length; i++) {
        extrudeSettings.depth = singleDepth[i];
        for (let o = 0; o < singleShapes[i].length; o++) {
          let singleGeo = new THREE.ExtrudeGeometry(singleShapes[i][o], extrudeSettings);
          singleGeo.translate(0, 0, depth - singleDepth[i] + 0.1);
          let cloned = singleLines[i][o].clone(); // making a clone of singlePath outline
          cloned.translateZ(depth); // moving the cloned one to top
          topSingleLines.push(cloned); // push cloned one to topSingleLines
          singleLines[i][o].translateZ(depth - singleDepth[i] + 0.1); // moving the original outline to the correct height (from bottom)
          // negGeo.merge(singleGeo)
          singlePathGeo.merge(singleGeo);
        }
      }
    }

    // ex_path
    let posMesh = this.rotateAndScale(this.fromGeometryToMesh(posGeo, 'pos')); // turn geometry into mesh and rotate/scale it
    this.addLinesToMesh(posMesh, lines, topLines, 'main'); // adding outlines(both topLines & bottomLines) to posMesh
    if (posMesh.geometry.faces.length === 0) {
      posMesh = null;
    }

    // in_paths
    let negMesh = this.rotateAndScale(this.fromGeometryToMesh(negGeo, 'neg'));
    if (negMesh.geometry.vertices.length === 0) {
      negMesh = null;
    }
    // pockets
    let pocketMesh = this.rotateAndScale(this.fromGeometryToMesh(pocketGeo, 'pocket'));
    this.addLinesToMesh(pocketMesh, pocketLines, topPocketLines, 'pocket'); // adding outlines(both topLines & bottomLines) to pocketMesh
    if (pocketMesh.geometry.vertices.length === 0) {
      pocketMesh = null;
    }
    // inner pockets
    let innerPocketMesh = this.rotateAndScale(this.fromGeometryToMesh(innerPocketGeo, 'innerPocket'));
    if (innerPocketMesh.geometry.vertices.length === 0) {
      innerPocketMesh = null;
    }
    // single paths
    let singlePathMesh = this.rotateAndScale(this.fromGeometryToMesh(singlePathGeo, 'singlePath'));
    this.addLinesToMesh(singlePathMesh, singleLines, topSingleLines, 'singlePath'); // adding outlines(both topLines & bottomLines) to singlePathMesh
    if (singlePathMesh.geometry.vertices.length === 0) {
      singlePathMesh = null;
    }

    // posMesh.add(negMesh)

    // return posMesh
    return {
      posMesh: posMesh,
      negMesh: negMesh,
      pocketMesh: pocketMesh,
      innerPocketMesh: innerPocketMesh,
      singlePathMesh: singlePathMesh
    };
  }

  rotateAndScale (mesh) {
    // var edges = new THREE.EdgesGeometry( mesh.geometry )
    // var outLine = new THREE.LineSegments( edges, new THREE.LineBasicMaterial( { color: 0x00A9B5 } ) )

    // var wireframe = new THREE.WireframeGeometry( mesh.geometry )
    // var outLine = new THREE.LineSegments( wireframe, new THREE.LineBasicMaterial( { color: 0x00A9B5 } ) )

    // mesh.add(outLine)

    const rx = -Math.PI * 0.5;

    mesh.rotation.set(rx, 0, 0);
    mesh.scale.set(scaleRate, scaleRate, scaleRate);

    return mesh;
  }

  rotateAndScaleSTL (mesh) {
    var edges = new THREE.EdgesGeometry(mesh.geometry);
    var outLine = new THREE.LineSegments(edges, new THREE.LineBasicMaterial({ color: 0x00A9B5 }));

    mesh.add(outLine);

    const rx = -Math.PI * 0.5;

    mesh.rotation.set(rx, 0, 0);
    mesh.scale.set(scaleRate, scaleRate, scaleRate);

    return mesh;
  }

  // extrude (coordinateArray, depth, innerShapes) {
  //   let shape = this.makeShape(coordinateArray)
  //   // shape.add(innerShape)
  //
  //   const extrudeSettings = {
  //     depth: depth,
  //     bevelSize: 0
  //   }
  //
  //   let extrude = new THREE.ExtrudeGeometry( shape, extrudeSettings )
  //
  //   // inner shapes must be seperated, put all inner shapes in one (THREE Shape) leads to chaos
  //   if(innerShapes.length>0){
  //     for(var i=0; i<innerShapes.length; i++){
  //       let innerExtrude = new THREE.ExtrudeGeometry( innerShapes[i], extrudeSettings )
  //       extrude.merge(innerExtrude)
  //     }
  //   }
  //
  //   const shapeMat = new THREE.MeshBasicMaterial({
  //     color: 0xbcbcbc,
  //     opacity: 0.5,
  //     transparent: true
  //   })
  //   let mesh = new THREE.Mesh(extrude, shapeMat)
  //
  //   // var edges = new THREE.EdgesGeometry( extrude )
  //   // var outLine = new THREE.LineSegments( edges, new THREE.LineBasicMaterial( { color: 0xffffff } ) )
  //   // return {"mesh": mesh, "outLine": outLine}
  //   // return {"mesh": mesh, "innerExtrude": innerExtrude}
  //   return mesh
  // }

  offset (spline, distance) {
    let normalPts = [];
    let twoTops = [];

    for (let y = 0; y <= 100; y++) { // divide for 100 pts
      let point = spline.getPointAt(y / 100);
      let pointVec = new THREE.Vector3(point.x, point.y, 0);

      let tangent = spline.getTangent(y / 100);
      // let extension1 = new THREE.Vector3(point.x+tangent.x, point.y+tangent.y, 0)
      // let extension2 = new THREE.Vector3(point.x+tangent.x*20, point.y+tangent.y*20, 0)
      let tangentH = Math.sqrt(Math.pow(tangent.x, 2) + Math.pow(tangent.y, 2));
      let sideB = tangent.x;
      let cosA = sideB / tangentH;
      // let angle = Math.acos(cosA) * (180 / Math.PI)    // degree
      let angle = Math.acos(cosA); // the tangent angle    // radian
      // let ratio = tangentH / knifeRad

      if (y === 0 || y === 100) { // save two top points
        let topPt = { x: point.x, y: point.y, angle: angle + 0.5 * Math.PI };
        twoTops.push(topPt);
      }

      // rotate tangent and get point coordinate
      let x1 = distance * Math.cos(angle + 0.5 * Math.PI); // vector
      let y1 = distance * Math.sin(angle + 0.5 * Math.PI); // vector
      let x2 = distance * Math.cos(angle - 0.5 * Math.PI); // vector
      let y2 = distance * Math.sin(angle - 0.5 * Math.PI); // vector

      let offsetPt1 = { x: point.x + x1, y: point.y + y1 };
      let offsetPt2 = { x: point.x + x2, y: point.y + y2 };
      let offsetPts = { sideA: offsetPt1, sideB: offsetPt2 };

      // visualization of vector line and normal line
      // let normalVec = new THREE.Vector3(offsetPt1.x, offsetPt1.y, 0)
      // let normalVec2 = new THREE.Vector3(offsetPt2.x, offsetPt2.y, 0)
      //
      // let tangentGeometry = new THREE.Geometry()
      // tangentGeometry.vertices.push( pointVec, extension1, extension2);
      //
      // let normalGeometry = new THREE.Geometry()
      // normalGeometry.vertices.push(normalVec, pointVec, normalVec2);
      //
      // const material = new THREE.LineBasicMaterial({
      //   color: 0x0000ff,
      //   opacity: 1,
      //   transparent: false,
      //   depthWrite: false,
      //   depthTest: false
      // })
      // let tangentLine = new THREE.Line(tangentGeometry, material)
      // let normalLine = new THREE.Line(normalGeometry, material)
      //
      // tangentLine.rotation.set(- Math.PI * 0.5, 0, 0)
      // tangentLine.scale.set(scaleRate, scaleRate, scaleRate)
      // normalLine.rotation.set(- Math.PI * 0.5, 0, 0)
      // normalLine.scale.set(scaleRate, scaleRate, scaleRate)
      //
      // parts.push(tangentLine)
      // parts.push(normalLine)

      normalPts.push(offsetPts);
    }

    let mainPts = [];
    for (let y = 0; y < normalPts.length; y++) {
      mainPts.push(normalPts[y].sideA);
    }

    for (let y = normalPts.length - 1; y >= 0; y--) {
      mainPts.push(normalPts[y].sideB);
    }

    return { outlinePts: mainPts, topPts: twoTops };
  }

  findMax (array) {
    let max = { x: array[0].x, y: array[0].y };
    for (let i = 1; i < array.length; i++) {
      if (array[i].x > max.x) {
        max.x = array[i].x;
      }
      if (array[i].y > max.y) {
        max.y = array[i].y;
      }
    }
    return max;
  }

  findMin (array) {
    let min = { x: array[0].x, y: array[0].y };
    for (let i = 1; i < array.length; i++) {
      if (array[i].x < min.x) {
        min.x = array[i].x;
      }
      if (array[i].y < min.y) {
        min.y = array[i].y;
      }
    }
    return min;
  }

  listingMovingData (paths) {
    // creating data for moving each object
    let movingData = [];
    let xD = 0;
    let yD = 0;

    for (let i = 0; i < paths.length; i++) {
      const pieceNum = paths[i].num;

      const maxX = this.findMax(paths[i].ex_path).x * scaleRate;
      const maxY = this.findMax(paths[i].ex_path).y * scaleRate;
      const minX = this.findMin(paths[i].ex_path).x * scaleRate;
      const minY = this.findMin(paths[i].ex_path).y * scaleRate;

      let tempContainer = []; // creating this format -> [ [{}], [{}], [{}, {}, {}],... ]
      for (let d = 0; d < pieceNum; d++) { // 1 or more than 1
        let thisData = { moveX: xD, moveY: yD, minX: minX, minY: minY };
        tempContainer.push(thisData);

        const d4x = (maxX - minX);
        const d4y = (maxY - minY);

        if (d4x > d4y) {
          yD = d4y + yD + 50 * scaleRate;
        } else {
          xD = d4x + xD + 50 * scaleRate;
        }
      }
      movingData.push(tempContainer);
    }
    return movingData;
  }

  fromGeometryToMesh (geometry, type) {
    const posMat = new THREE.MeshLambertMaterial({
      color: 0x9f9f9f,
      opacity: 0.7,
      transparent: true,
      // visible: false
      polygonOffset: true,
      polygonOffsetFactor: 3.0,
      polygonOffsetUnits: 4.0
    });

    const negMat = new THREE.MeshBasicMaterial({
      color: 0x656565,
      opacity: 0.5,
      transparent: true,
      polygonOffset: true,
      polygonOffsetFactor: 0.5,
      polygonOffsetUnits: 4.0
    });

    const pocketMat = new THREE.MeshBasicMaterial({
      color: 0xff0000,
      opacity: 0.5,
      transparent: true,
      polygonOffset: true,
      polygonOffsetFactor: 2.0,
      polygonOffsetUnits: 4.0
    });

    const innerPocketMat = new THREE.MeshBasicMaterial({
      color: 0xff7e00,
      opacity: 0.5,
      transparent: true,
      polygonOffset: true,
      polygonOffsetFactor: 1.0,
      polygonOffsetUnits: 4.0
    });

    const singlePathMat = new THREE.MeshBasicMaterial({
      color: 0x001eff,
      opacity: 0.5,
      transparent: true,
      polygonOffset: true,
      polygonOffsetFactor: 1.0,
      polygonOffsetUnits: 4.0
    });

    let mesh;
    switch (type) {
      case 'pos':
        mesh = new THREE.Mesh(geometry, posMat);
        break;
      case 'neg':
        mesh = new THREE.Mesh(geometry, negMat);
        break;
      case 'pocket':
        mesh = new THREE.Mesh(geometry, pocketMat);
        break;
      case 'innerPocket':
        mesh = new THREE.Mesh(geometry, innerPocketMat);
        break;
      case 'singlePath':
        mesh = new THREE.Mesh(geometry, singlePathMat);
        break;
    }

    return mesh;
  }

  nestEachMesh (mesh, index, movingData, amount) {
    if (amount === 1) { // when only 1 piece for this path
      mesh.translateX(-movingData[index][0].minX); // move Southwest corner to (0,0)
      mesh.translateY(-movingData[index][0].minY);
      mesh.translateX(movingData[index][0].moveX); // move according to movingData
      mesh.translateY(movingData[index][0].moveY);
      this.container.add(mesh);
    } else { // when multiple pieces for this path
      mesh.translateX(-movingData[index][0].minX); // move Southwest corner to (0,0)
      mesh.translateY(-movingData[index][0].minY);
      for (let u = 0; u < amount; u++) {
        let copiedMesh = mesh.clone();
        copiedMesh.translateX(movingData[index][u].moveX); // move according to movingData
        copiedMesh.translateY(movingData[index][u].moveY);
        this.container.add(copiedMesh);
      }
    }
  }

  displayEachMesh (mesh, toCenterData, amount) {
    if (!mesh) {
      return;
    }

    mesh.translateX(-toCenterData.minX); // move Southwest corner to (0,0)
    mesh.translateY(-toCenterData.minY);

    // console.log(mesh.geometry);
    // mesh.geometry.computeBoundingBox()
    // console.log(mesh.geometry.boundingBox);

    if (amount === 1) { // when only 1 piece for this path
      this.container.add(mesh);
    } else { // when multiple pieces for this path
      for (let u = 0; u < amount; u++) {
        let copiedMesh = mesh.clone();
        this.container.add(copiedMesh);
      }
    }
  }

  getMinPosition (paths) {
    let minData = {
      minX: this.findMin(paths[0].ex_path).x * scaleRate,
      minY: this.findMin(paths[0].ex_path).y * scaleRate
    };
    for (const path of paths) {
      const minX = this.findMin(path.ex_path).x * scaleRate;
      const minY = this.findMin(path.ex_path).y * scaleRate;

      if (minX < minData.minX) {
        minData.minX = minX;
      }
      if (minY < minData.minY) {
        minData.minY = minY;
      }
    }
    return minData;
  }

  getMinPositionSTL (mesh) {
    mesh.geometry.computeBoundingBox();
    // console.log(mesh.geometry.boundingBox);
    let min = mesh.geometry.boundingBox.min;
    return { x: min.x * scaleRate, y: min.y * scaleRate };
  }

  parse (pathJson, report) {
    // to initialize container first
    super.reset();

    this.pathJson = pathJson;

    let parts = [];
    // let xD = 0
    // let yD = 0
    // let allD4X = 0
    // let allD4Y = 0
    const knifeDepth = 6;
    const thickness = Number(pathJson.thickness);
    let knifeDia;

    if (thickness === 30 || thickness === 36) {
      knifeDia = 12.7;
    } else {
      knifeDia = 6.35;
    }

    let knifeRad = knifeDia / 2;

    let movingData;
    let allGeoMinData;
    if (pathJson.paths[0].ex_path.length !== 0) { // when data is not STL
      // creating data for moving each object
      movingData = this.listingMovingData(pathJson.paths); // when nesting is needed

      allGeoMinData = this.getMinPosition(pathJson.paths); // when without nesting
    }
    // creating data for moving each object
    // let movingData = this.listingMovingData(pathJson.paths) // when nesting is needed
    //
    // let allGeoMinData = this.getMinPosition(pathJson.paths) // when without nesting

    if (pathJson.paths.length > 100) { // multi-threads
      if (window.Worker) {
        const workerAmount = 5;
        let num4EachWorker = parseInt(pathJson.paths.length / workerAmount);
        let leftNum = pathJson.paths.length % workerAmount;
        // let allNum = workerAmount * num4EachWorker

        let originalData = {
          json: pathJson, // base data
          thickness: thickness, // to extrude
          radius: knifeRad, // for offet
          iteration: num4EachWorker, // iteration before the last piece
          lastPiece: false // if(lastPiece)  fixed iteration -> no stop until end
        };

        for (let t = 0; t < workerAmount; t++) {
          if (t === workerAmount - 1) { // lastPiece or not
            originalData.lastPiece = true;
          }

          let startIndex = t * num4EachWorker;
          let data = {
            pathData: originalData,
            index: startIndex
          };

          let worker = new Worker('./static/workers/myWorker.js');

          worker.onmessage = (e) => { // arrow function makes "this" the original "this"
            const positionPos = e.data.positionPos;
            const positionNeg = e.data.positionNeg;
            const index = e.data.index;

            const pieceNum = pathJson.paths[index].num;

            const posGeometry = new THREE.BufferGeometry();
            posGeometry.setAttribute('position', new THREE.BufferAttribute(e.data.positionPos, 3));
            let posMesh = this.fromGeometryToMesh(posGeometry, 'pos');
            var edgesPos = new THREE.EdgesGeometry(posMesh.geometry);
            var outLinePos = new THREE.LineSegments(edgesPos, new THREE.LineBasicMaterial({ color: 0x00A9B5 }));
            posMesh.add(outLinePos);

            const negGeometry = new THREE.BufferGeometry();
            negGeometry.setAttribute('position', new THREE.BufferAttribute(e.data.positionNeg, 3));
            let negMesh = this.fromGeometryToMesh(negGeometry, 'neg');
            var edgesNeg = new THREE.EdgesGeometry(negMesh.geometry);
            var outLineNeg = new THREE.LineSegments(edgesNeg, new THREE.LineBasicMaterial({ color: 0x00A9B5 }));
            negMesh.add(outLineNeg);

            const pocketGeometry = new THREE.BufferGeometry();
            pocketGeometry.setAttribute('position', new THREE.BufferAttribute(e.data.positionPocket, 3));
            let pocketMesh = this.fromGeometryToMesh(pocketGeometry, 'pocket');
            var edgesPocket = new THREE.EdgesGeometry(pocketMesh.geometry);
            var outLinePocket = new THREE.LineSegments(edgesPocket, new THREE.LineBasicMaterial({ color: 0x00A9B5 }));
            pocketMesh.add(outLinePocket);

            const innerPocketGeometry = new THREE.BufferGeometry();
            innerPocketGeometry.setAttribute('position', new THREE.BufferAttribute(e.data.positionInnerPocket, 3));
            let innerPocketMesh = this.fromGeometryToMesh(innerPocketGeometry, 'innerPocket');
            var edgesInnerPocket = new THREE.EdgesGeometry(innerPocketMesh.geometry);
            var outLineInnerPocket = new THREE.LineSegments(edgesInnerPocket, new THREE.LineBasicMaterial({ color: 0x00A9B5 }));
            innerPocketMesh.add(outLineInnerPocket);

            const singlePathGeometry = new THREE.BufferGeometry();
            singlePathGeometry.setAttribute('position', new THREE.BufferAttribute(e.data.positionSinglePath, 3));
            let singlePathMesh = this.fromGeometryToMesh(singlePathGeometry, 'singlePath');
            var edgesSinglePath = new THREE.EdgesGeometry(singlePathMesh.geometry);
            var outLineSinglePath = new THREE.LineSegments(edgesSinglePath, new THREE.LineBasicMaterial({ color: 0x00A9B5 }));
            singlePathMesh.add(outLineSinglePath);

            const rx = -Math.PI * 0.5;

            if (posMesh.geometry.attributes.position.array.length < 3) { // if < 3, it becomes faceless, it's a line instead of a geometry
              posMesh = null;
            } else {
              this.rotateAndScale(posMesh);
            }
            if (negMesh.geometry.attributes.position.array.length < 3) {
              negMesh = null;
            } else {
              this.rotateAndScale(negMesh);
              // negMesh.rotation.set(rx, 0, 0)
              // negMesh.scale.set(scaleRate, scaleRate, scaleRate)
              // this.displayEachMesh(negMesh, allGeoMinData, pieceNum)
            }

            if (pocketMesh.geometry.attributes.position.array.length < 3) {
              pocketMesh = null;
            } else {
              this.rotateAndScale(pocketMesh);
            }
            if (innerPocketMesh.geometry.attributes.position.array.length < 3) {
              innerPocketMesh = null;
            } else {
              this.rotateAndScale(innerPocketMesh);
            }
            if (singlePathMesh.geometry.attributes.position.array.length < 3) {
              singlePathMesh = null;
            } else {
              this.rotateAndScale(singlePathMesh);
            }

            // nesting
            // this.nestEachMesh(mesh, index, movingData, pieceNum)

            // without nesting
            this.displayEachMesh(posMesh, allGeoMinData, pieceNum);
            this.displayEachMesh(negMesh, allGeoMinData, pieceNum);

            this.displayEachMesh(pocketMesh, allGeoMinData, pieceNum);
            this.displayEachMesh(innerPocketMesh, allGeoMinData, pieceNum);
            this.displayEachMesh(singlePathMesh, allGeoMinData, pieceNum);

            this.boundingBox.update(this.container);
            this.updateGrid();
          };
          worker.postMessage(data);
        }
      }
    } else { // single-thread
      // partial group(ex/in_path) -> group(ex & in) -> layers -> parts(complete object path)
      let stlMinData = [];
      for (let i = 0; i < pathJson.paths.length; i++) {
        if (pathJson.paths[i].ex_path.length !== 0) {
          // GIST: GENERATE SHAPE & OUTLINE -> EXTRUDE SHAPES & ADD OUTLINES -> DISPLAY EACH RESULT

          let shapes = [];
          let lines = [];
          let pocketShapes = [];
          let pocketLines = [];
          let singleShapes = [];
          let singleLines = [];
          let copied = false;
          const pieceNum = pathJson.paths[i].num;
          const pocketDepth = pathJson.paths[i].po_deepness;
          const singleDepth = pathJson.paths[i].single_deepness;
          // const thickness = Number(pathJson.paths[i].thickness)

          if (pieceNum > 1) {
            copied = true;
          }

          // let max = this.findMax(pathJson.paths[i].ex_path)
          // let min = this.findMin(pathJson.paths[i].ex_path)

          // group.add(this.groupLine(pathJson.paths[i].ex_path))
          const exShape = this.makeShape(pathJson.paths[i].ex_path);
          if (!exShape)
          {
            continue;
          }  
          shapes.push(exShape);
          lines.push(this.makeOutline(pathJson.paths[i].ex_path)); // push outline of exPath to lines[]
          // use "Array-or-not" to recognize the content
          // if in_paths[0] is "array" -> more than one in_paths-set (more than one hole)
          if (pathJson.paths[i].in_paths.length > 0 && !Array.isArray(pathJson.paths[i].in_paths[0])) {
            // group.add( this.groupLine(pathJson.paths[i].in_paths) )
            const inShape = this.makeShape(pathJson.paths[i].in_paths);
            if (inShape) {
              shapes.push(inShape);
              lines.push(this.makeOutline(pathJson.paths[i].in_paths)); // push outline of inPaths to lines[] (when in_paths is singular)
            }
          } else if (pathJson.paths[i].in_paths.length > 0 && Array.isArray(pathJson.paths[i].in_paths[0])) {
            for (let k = 0; k < pathJson.paths[i].in_paths.length; k++) {
              // group.add( this.groupLine(pathJson.paths[i].in_paths[k]) )
              const inShape = this.makeShape(pathJson.paths[i].in_paths[k]);
              if (inShape) {
                shapes.push(inShape);
                lines.push(this.makeOutline(pathJson.paths[i].in_paths[k])); // push outline of inPaths to lines[] (when in_paths are plural)
              }
            }
          }

          // console.log(pathJson.paths[i].pocket);

          if (pathJson.paths[i].pocket) {
            let pocket = pathJson.paths[i].pocket;
            if (pocket.length > 0) {
              for (let p = 0; p < pocket.length; p++) {
                let tempPo = []; // array of pocket shapes (the structure follows pathJson.paths[i].pocket)
                let tempLines = []; // array of pocket outlines (the structure follows pathJson.paths[i].pocket)

                if (pocket[p].ex_pocket.length > 0) {
                  const exPocketShape = this.makeShape(pocket[p].ex_pocket);
                  if (exPocketShape) {
                    tempPo.push(exPocketShape);
                    tempLines.push(this.makeOutline(pocket[p].ex_pocket));
                  }
                }

                if (pocket[p].in_pocket.length > 0) {
                  for (let inPocket of pocket[p].in_pocket) {
                    const inPocketShape = this.makeShape(inPocket);
                    if (inPocketShape) {
                      tempPo.push(inPocketShape);
                      tempLines.push(this.makeOutline(inPocket));
                    }
                  }
                }

                pocketShapes.push(tempPo);
                pocketLines.push(tempLines);
              }
            }

            // console.log("pocketShapes");
            // console.log(pocketShapes);
          }

          if (pathJson.paths[i].single_paths) {
            let singlePaths = pathJson.paths[i].single_paths;
            for (let a = 0; a < singlePaths.length; a++) {
              let tempSi = []; // container for each singlePaths shape array
              let tempLines = []; // container for each singlePaths outline array

              if (singlePaths[a].length !== 1) { // operation for single path
                let originalPtArray = [];
                for (let y = 0; y < singlePaths[a].length; y++) {
                  let vector = new THREE.Vector2(singlePaths[a][y].x, singlePaths[a][y].y);
                  originalPtArray.push(vector);
                }

                let spline = new THREE.SplineCurve(originalPtArray);

                let offsetData = this.offset(spline, knifeRad);

                let outlinePts = offsetData.outlinePts;
                let twoTops = offsetData.topPts;

                const outlineShape = this.makeShape(outlinePts);
                if (outlineShape) {
                  tempSi.push(outlineShape);
                  tempLines.push(this.makeOutline(outlinePts));
                }

                for (let y = 0; y < twoTops.length; y++) {
                  let curve = new THREE.EllipseCurve(
                    twoTops[y].x, twoTops[y].y, // ax, aY
                    knifeRad, knifeRad, // xRadius, yRadius
                    0, 2 * Math.PI // aStartAngle, aEndAngle
                  );
                  let arcPts = curve.getPoints(10);
                  arcPts.splice(arcPts.length - 1, 1);

                  const arcShape = this.makeShape(arcPts);
                  if (arcShape) {
                    tempSi.push(arcShape);
                    tempLines.push(this.makeOutline(arcPts));
                  }
                }

                singleShapes.push(tempSi);
                singleLines.push(tempLines);
              } else { // operation for whole
                let curve = new THREE.EllipseCurve(
                  singlePaths[a][0].x, singlePaths[a][0].y, // ax, aY
                  knifeRad, knifeRad // xRadius, yRadius
                );

                let points = curve.getPoints(20);
                points.splice(points.length - 1, 1);

                const wholeShape = this.makeShape(points);
                if (wholeShape) {
                  tempSi.push(wholeShape);
                  tempLines.push(this.makeOutline(points));
                }

                singleShapes.push(tempSi);
                singleLines.push(tempLines);
              }
            }
          }

          // if(thickness % knifeDepth !== 0){
          //   group.add( group.clone().translateZ(-thickness % knifeDepth) )
          //   for(let o=1; o<thickness/knifeDepth +1; o++){
          //     group.add( group.clone().translateZ(-knifeDepth*o + thickness % knifeDepth) )
          //   }
          // } else {
          //   for(let o=thickness/knifeDepth; o>0; o--){
          //     group.add( group.clone().translateZ(-knifeDepth*o) )
          //   }
          // }

          // let mesh = this.extrude(pathJson.paths[i].ex_path, thickness, shapes)
          let mesh = this.extrude(shapes, thickness, pocketShapes, pocketDepth, singleShapes, singleDepth, lines, pocketLines, singleLines);
          //
          // var edges = new THREE.EdgesGeometry( mesh.geometry )
          // var outLine = new THREE.LineSegments( edges, new THREE.LineBasicMaterial( { color: 0x00A9B5 } ) )
          // //
          // mesh.add(outLine)
          // //
          // const rx = - Math.PI * 0.5
          //
          // // group.rotation.set(rx, 0, 0)
          // // group.scale.set(scaleRate, scaleRate, scaleRate)
          // mesh.rotation.set(rx, 0, 0)
          // mesh.scale.set(scaleRate, scaleRate, scaleRate)
          // mesh.translateZ(-thickness * scaleRate)
          // mesh.translateZ(thickness*0.6 * scaleRate)

          // this.nestEachMesh(mesh, i, movingData, pieceNum)  // nesting & adding to container
          this.displayEachMesh(mesh.posMesh, allGeoMinData, pieceNum); // without nesting & adding to container
          this.displayEachMesh(mesh.negMesh, allGeoMinData, pieceNum); // without nesting & adding to container

          this.displayEachMesh(mesh.pocketMesh, allGeoMinData, pieceNum);
          this.displayEachMesh(mesh.innerPocketMesh, allGeoMinData, pieceNum);
          this.displayEachMesh(mesh.singlePathMesh, allGeoMinData, pieceNum);

          this.boundingBox.update(this.container);
          this.updateGrid();

          // mesh.geometry.computeBoundingBox()
          // const maxX = mesh.geometry.boundingBox.max.x * scaleRate
          // const maxY = mesh.geometry.boundingBox.max.y * scaleRate
          // const minX = mesh.geometry.boundingBox.min.x * scaleRate
          // const minY = mesh.geometry.boundingBox.min.y * scaleRate
          //
          // // move the geometry to (0, 0) first
          // mesh.translateX(-minX)
          // mesh.translateY(-minY)
          // // mesh.geometry.translate(-minX, -minY, 0)
          //
          // const d4x = (maxX - minX)
          // const d4y = (maxY - minY)
          //
          // // check if >1 object with the same shape
          // if (copied) {
          //   for (let j = 0; j < pieceNum; j++) {
          //     // let copiedGroup = group.clone().translateX(-minX + xD).translateY(-minY + yD)
          //     // copiedGroup.translateX(d4x*1.05*j)
          //     // let copiedMesh = mesh.clone()
          //     // copiedMesh.translateX(d4x*1.05*j)
          //     // let copiedMesh = mesh.clone()
          //     let copiedMesh = mesh.clone()
          //     copiedMesh.translateX(xD)
          //     copiedMesh.translateY(yD)
          //
          //     // push to layers "from top to bottom"
          //     // for (let o = thickness / knifeDepth; o > 0; o--) {
          //     //   layers.add(copiedGroup.clone().translateZ(-knifeDepth * scaleRate * o))
          //     // }
          //     // layers.add(copiedGroup)
          //
          //     // + 50*scaleRate is for making tangentH between each object
          //     if (d4x > d4y) {
          //       yD = d4y + yD + 50*scaleRate
          //     } else {
          //       xD = d4x + xD + 50*scaleRate
          //     }
          //
          //     // if(xD > 2*yD){
          //     //   yD = d4y + yD + 50*scaleRate
          //     // } else if(yD > 2*xD){
          //     //   xD = d4x + xD + 50*scaleRate
          //     // } else{
          //     //   if (d4x > d4y) {
          //     //     yD = d4y + yD + 50*scaleRate
          //     //   } else {
          //     //     xD = d4x + xD + 50*scaleRate
          //     //   }
          //     // }
          //
          //     // allD4X = allD4X + xD
          //     // allD4Y = allD4Y + yD
          //
          //     // parts.push(layers)
          //     parts.push(copiedMesh)
          //     // layers = new THREE.Group()
          //   }
          // } else {
          //   // else just .push() one time
          //   // for (let o = thickness / knifeDepth; o > 0; o--) {
          //   //   layers.add(group.clone().translateZ(-knifeDepth * scaleRate * o).translateX(-minX + xD).translateY(-minY + yD))
          //   // }
          //   // layers.add(group.translateX(-minX + xD).translateY(-minY))
          //
          //   // mesh.geometry.translate(xD, 0, 0)
          //   mesh.translateX(xD)
          //   mesh.translateY(yD)
          //
          //   // + 50*scaleRate is for making tangentH between each object
          //   if (d4x > d4y) {
          //     yD = d4y + yD + 50*scaleRate
          //   } else {
          //     xD = d4x + xD + 50*scaleRate
          //   }
          //
          //   // if(xD > 2*yD){
          //   //   yD = d4y + yD + 50*scaleRate
          //   // } else if(yD > 2*xD){
          //   //   xD = d4x + xD + 50*scaleRate
          //   // } else{
          //   //   if (d4x > d4y) {
          //   //     yD = d4y + yD + 50*scaleRate
          //   //   } else {
          //   //     xD = d4x + xD + 50*scaleRate
          //   //   }
          //   // }
          //
          //   // allD4X = allD4X + xD
          //   // allD4Y = allD4Y + yD
          //
          //   // parts.push(layers)
          //   parts.push(mesh)
          // }
        } else { // when pathJson is STL data
          for (let q = 0; q < pathJson.paths[i].stl.length; q++) {
            const dataUrl = 'data:model/stl;base64,' + pathJson.paths[i].stl[q].data;
            const loader = new STLLoader();

            loader.load(dataUrl, (geometry) => {
              const mat = new THREE.MeshLambertMaterial({
                color: 0x9f9f9f,
                opacity: 0.7,
                transparent: true,
                polygonOffset: true,
                polygonOffsetFactor: 3.0,
                polygonOffsetUnits: 4.0
              });
              let mesh = new THREE.Mesh(geometry, mat);
              mesh = this.rotateAndScaleSTL(mesh);
              stlMinData.push(this.getMinPositionSTL(mesh)); // collecting the minData of each piece
              this.container.add(mesh);
              // console.log(stlMinData);

              // moving all pieces to center without influencing the relative position between each piece
              if (i === pathJson.paths.length - 1) { // last piece
                let minX = stlMinData[0].x;
                let minY = stlMinData[0].y;
                for (let minData of stlMinData) { // compare minData of each piece (finding the minimal set)
                  if (minData.x < minX) {
                    minX = minData.x;
                  }
                  if (minData.y < minY) {
                    minY = minData.y;
                  }
                }
                for (let eachMesh of this.container.children) { // moving each mesh based on minimal set of minData
                  eachMesh.translateX(-minX);
                  eachMesh.translateY(-minY);
                }
                this.boundingBox.update(this.container); // update boundingBox after each mesh is moved
                this.updateGrid();
              }
            });
          }
        }
      }

      // parts.forEach((part) => {
      //   this.container.add(part)
      // })

      // this.boundingBox.update(this.container)
    }

    // console.log(parts);

    // parts.forEach((part) => {
    //   this.container.add(part)
    // })

    // this.boundingBox.update(this.container)
    // this.updateGrid();
  }

  load (path, report, onLoad, onProgress, onError) {
    const loader = new THREE.FileLoader();
    loader.load(
      path,
      (text) => {
        this.parse(JSON.parse(text), report);
        if (onLoad !== undefined) {
          onLoad(this);
        }
      },
      onProgress, onError
    );
  }

  get exportComponents () {
    return [
      this.pathJson
    ];
  }
}



// WEBPACK FOOTER //
// ./src/assets/javascripts/controllers/PathController.js